import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MessageSquare } from "lucide-react";
import {
  createAssistantThread,
  getAssistantThread,
  listAssistantThreads,
  resolveCommandItemThread,
  sendAssistantMessage,
  updateAssistantThread,
} from "@/api/assistant";
import { ApiError } from "@/api/client";
import { getSalesMove } from "@/api/sales-moves";
import { AssistantRail } from "@/components/assistant/assistant-rail";
import { AssistantEmptyState } from "@/components/assistant/assistant-empty-state";
import { AssistantComposer } from "@/components/assistant/assistant-composer";
import { AssistantThreadView } from "@/components/assistant/assistant-thread";
import { CreditSettingsDialog } from "@/components/assistant/credit-settings-dialog";
import { MobileSheet } from "@/components/mobile-sheet";
import type { AssistantMessage, StreamEvent } from "@sales-ops-amplify/shared";

const SALES_MOVE_STARTERS = [
  "Explain this recommendation",
  "Prepare me for the call",
  "Draft the reply",
  "Review the evidence",
  "Summarize the account",
  "Identify missing stakeholders",
  "Compare alternatives",
  "Review deal risk",
  "Draft the proposal",
  "Recommend advance versus nurture",
] as const;

export function AiAssistantPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const threadId = searchParams.get("thread");
  const salesMoveId = searchParams.get("salesMoveId");
  const generationId = searchParams.get("generationId") ?? undefined;
  const originBucket = searchParams.get("originBucket");
  const originCursor = searchParams.get("originCursor");
  const originPageSize = searchParams.get("originPageSize");

  const [railCollapsed, setRailCollapsed] = useState(false);
  const [threadsSheetOpen, setThreadsSheetOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [contextProductIds, setContextProductIds] = useState<string[]>([]);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [running, setRunning] = useState(false);
  const [streamState, setStreamState] = useState<{
    text: string;
    tools: Array<{
      name: string;
      label?: string;
      args?: Record<string, unknown>;
      href?: string;
    }>;
    verb?: string;
    steps?: string[];
    durationMs?: number;
  } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const resolvingCommandThreadRef = useRef<string | null>(null);

  const threadsQuery = useQuery({
    queryKey: ["assistant-threads"],
    queryFn: listAssistantThreads,
  });

  const salesMoveQuery = useQuery({
    queryKey: ["sales-move", salesMoveId, generationId],
    queryFn: () => getSalesMove(salesMoveId!, generationId),
    enabled: Boolean(salesMoveId),
  });

  const salesMove = salesMoveQuery.data?.move;
  const salesMoveTaskTitle = useMemo(() => {
    if (!salesMove) return undefined;
    const parts = [
      salesMove.recommendedAction,
      salesMove.accountName !== "Unknown" ? salesMove.accountName : undefined,
    ].filter(Boolean);
    return parts.join(" · ") || undefined;
  }, [salesMove]);

  useEffect(() => {
    if (!salesMove || threadId) return;
    if (resolvingCommandThreadRef.current === salesMove.fingerprint) return;
    resolvingCommandThreadRef.current = salesMove.fingerprint;

    void resolveCommandItemThread({
      commandItemId: salesMove.id,
      generationId,
      contextProductIds,
    })
      .then(({ thread }) => {
        setSearchParams((current) => {
          const next = new URLSearchParams(current);
          next.set("thread", thread.threadId);
          return next;
        }, { replace: true });
        void queryClient.invalidateQueries({ queryKey: ["assistant-threads"] });
      })
      .finally(() => {
        resolvingCommandThreadRef.current = null;
      });
  }, [
    contextProductIds,
    generationId,
    queryClient,
    salesMove,
    setSearchParams,
    threadId,
  ]);
  const salesMoveContextBlock = useMemo(() => {
    if (!salesMove) return "";
    return [
      `Sales move context (id=${salesMove.id}):`,
      `Recommended: ${salesMove.recommendedAction}`,
      `Account: ${salesMove.accountName ?? "—"}`,
      `Person: ${salesMove.relevantPerson?.name ?? "—"}`,
      `Deal: ${salesMove.dealStage ?? "—"} ${salesMove.dealValue != null ? `$${salesMove.dealValue}` : ""}`.trim(),
      `Situation: ${salesMove.situationSummary}`,
      `Why now: ${salesMove.whyNow}`,
      salesMove.expectedOutcome ? `Expected: ${salesMove.expectedOutcome}` : "",
      salesMove.riskOfWaiting ? `Risk of waiting: ${salesMove.riskOfWaiting}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }, [salesMove]);

  const loadThread = useCallback(async (id: string) => {
    const res = await getAssistantThread(id);
    setMessages(res.messages);
    setContextProductIds(res.thread.contextProductIds ?? []);
    setStreamState(null);
  }, []);

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      setStreamState(null);
      return;
    }
    void loadThread(threadId).catch(() => {
      setSearchParams({}, { replace: true });
    });
  }, [threadId, loadThread, setSearchParams]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, streamState]);

  function goNewChat() {
    abortRef.current?.abort();
    setSearchParams({}, { replace: true });
    setMessages([]);
    setStreamState(null);
  }

  function selectThread(id: string) {
    setSearchParams({ thread: id }, { replace: true });
  }

  async function archiveThread(id: string) {
    const thread = threads.find((item) => item.threadId === id);
    const ok = window.confirm(
      `Archive “${thread?.title || "this chat"}”? It will be removed from the chat list.`,
    );
    if (!ok) return;

    await updateAssistantThread(id, { status: "archived" });
    await queryClient.invalidateQueries({ queryKey: ["assistant-threads"] });
    if (threadId === id) goNewChat();
  }

  async function persistContext(ids: string[]) {
    setContextProductIds(ids);
    if (threadId) {
      await updateAssistantThread(threadId, { contextProductIds: ids });
    }
  }

  async function send(message: string, explicitThreadId?: string) {
    // Ask AI must resolve the Command Item's stable thread before sending.
    if (salesMove && !threadId && !explicitThreadId) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(true);
    setStreamState({ text: "", tools: [], verb: "Worked" });

    const activeThread = explicitThreadId ?? threadId ?? undefined;
    // Keep the visible chat body as the user's task/request only.
    // Sales-move context is sent separately for the model (taskContext).

    if (activeThread) {
      const optimistic: AssistantMessage = {
        PK: "",
        SK: "",
        EntityType: "ASSISTANT_MESSAGE",
        userId: "",
        threadId: activeThread,
        messageId: `local-${Date.now()}`,
        seq: (messages[messages.length - 1]?.seq ?? 0) + 1,
        role: "user",
        blocks: [{ type: "text", text: message }],
        feedback: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
    }

    const runStream = (confirmCredits: boolean) =>
      sendAssistantMessage(
        {
          message,
          threadId: activeThread,
          contextProductIds,
          confirmCredits,
          ...(salesMoveTaskTitle ? { taskTitle: salesMoveTaskTitle } : {}),
          ...(salesMoveContextBlock
            ? { taskContext: salesMoveContextBlock }
            : {}),
        },
        (event: StreamEvent, m) => {
          if (m.threadId && m.threadId !== threadId) {
            setSearchParams({ thread: m.threadId }, { replace: true });
          }
          if (event.type === "text") {
            setStreamState((prev) => {
              const raw = (prev?.text ?? "") + event.text;
              const text = raw
                .replace(/<<act:\w+\s*\{[\s\S]*?\}>>/g, "")
                .replace(/<<act:\w+\s*>>/g, "")
                .replace(/^Thinking:\s*.+$/gim, "")
                .trimStart();
              const steps = [
                ...(prev?.steps ?? []),
                ...[...raw.matchAll(/^Thinking:\s*(.+)$/gim)].map((mm) => mm[1]),
              ].filter((s, i, arr) => arr.indexOf(s) === i);
              return {
                text,
                tools: prev?.tools ?? [],
                verb: prev?.verb,
                steps: steps.length ? steps : prev?.steps,
                durationMs: prev?.durationMs,
              };
            });
          } else if (event.type === "tool") {
            setStreamState((prev) => ({
              text: prev?.text ?? "",
              tools: [
                ...(prev?.tools ?? []),
                {
                  name: event.name,
                  label: event.label,
                  args: event.args,
                  href: event.href,
                },
              ],
              verb: prev?.verb,
              steps: prev?.steps,
              durationMs: prev?.durationMs,
            }));
          } else if (event.type === "status" && event.label.endsWith("…")) {
            setStreamState((prev) => ({
              text: prev?.text ?? "",
              tools: prev?.tools ?? [],
              verb: event.label.replace(/…$/, ""),
              steps: prev?.steps,
              durationMs: prev?.durationMs,
            }));
          } else if (
            event.type === "status" &&
            event.label === "message_saved"
          ) {
            setStreamState((prev) => ({
              text: prev?.text ?? "",
              tools: prev?.tools ?? [],
              verb: m.verb ?? prev?.verb,
              steps: m.steps ?? prev?.steps,
              durationMs: m.durationMs ?? prev?.durationMs,
            }));
          }
        },
        ac.signal,
      );

    try {
      let meta;
      try {
        meta = await runStream(false);
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.status === 402 &&
          (err.body as { error?: string })?.error ===
            "credit_confirmation_required"
        ) {
          const ok = window.confirm(
            "This assistant reply will use AI credits. Continue?",
          );
          if (!ok) throw err;
          meta = await runStream(true);
        } else {
          throw err;
        }
      }

      const finalId = meta.threadId ?? activeThread;
      if (finalId) {
        await loadThread(finalId);
        if (!threadId && meta.threadId) {
          setSearchParams({ thread: meta.threadId }, { replace: true });
        }
      }
      void queryClient.invalidateQueries({ queryKey: ["assistant-threads"] });
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        console.error(err);
      }
    } finally {
      setRunning(false);
      setStreamState(null);
    }
  }

  async function onPreset(preset: {
    id: string;
    name: string;
    text: string;
    autorun?: boolean;
  }) {
    if (salesMove && !threadId) return;
    if (!threadId) {
      const { thread } = await createAssistantThread({
        title: preset.name,
        contextProductIds,
      });
      setSearchParams({ thread: thread.threadId }, { replace: true });
      await send(preset.text, thread.threadId);
      return;
    }
    await send(preset.text);
  }

  const threads = (threadsQuery.data?.threads ?? []).filter(
    (t) => t.status !== "archived",
  );
  const todayContextHref = useMemo(() => {
    const params = new URLSearchParams();
    if (originBucket && originBucket !== "ALL_ACTIVE") {
      params.set("bucket", originBucket);
    }
    if (
      originPageSize &&
      ["10", "20", "30", "40", "50"].includes(originPageSize) &&
      originPageSize !== "20"
    ) {
      params.set("pageSize", originPageSize);
    }
    // While loading, preserve the intended item. On 404/deletion, fall back
    // to the first item in the originating bucket.
    if (salesMove || salesMoveQuery.isLoading) {
      if (salesMoveId) params.set("move", salesMoveId);
      if (originCursor) params.set("cursor", originCursor);
    }
    const query = params.toString();
    return query ? `/?${query}` : "/";
  }, [
    originBucket,
    originCursor,
    originPageSize,
    salesMove,
    salesMoveId,
    salesMoveQuery.isLoading,
  ]);
  const resolvingCommandThread = Boolean(salesMove && !threadId);
  const showEmpty = !threadId && messages.length === 0 && !streamState;

  return (
    <div className="flex h-full min-h-0 overflow-hidden rounded-lg border border-border bg-background">
      <div className="hidden h-full md:block">
        <AssistantRail
          collapsed={railCollapsed}
          onToggleCollapse={() => setRailCollapsed((c) => !c)}
          threads={threads}
          activeThreadId={threadId}
          onNewChat={goNewChat}
          onSelectThread={selectThread}
          onArchiveThread={(id) => void archiveThread(id)}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      </div>

      <MobileSheet
        open={threadsSheetOpen}
        onClose={() => setThreadsSheetOpen(false)}
        title="Threads"
        variant="full"
      >
        <AssistantRail
          embedded
          collapsed={false}
          onToggleCollapse={() => undefined}
          threads={threads}
          activeThreadId={threadId}
          onNewChat={() => {
            goNewChat();
            setThreadsSheetOpen(false);
          }}
          onSelectThread={(id) => {
            selectThread(id);
            setThreadsSheetOpen(false);
          }}
          onArchiveThread={(id) => {
            void archiveThread(id);
            setThreadsSheetOpen(false);
          }}
          onOpenSettings={() => {
            setThreadsSheetOpen(false);
            setSettingsOpen(true);
          }}
        />
      </MobileSheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 md:hidden">
          <button
            type="button"
            onClick={() => setThreadsSheetOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-surface-hover px-2.5 py-1.5 text-xs font-medium text-foreground"
          >
            <MessageSquare className="size-3.5" />
            Threads
          </button>
          <button
            type="button"
            onClick={goNewChat}
            className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium"
          >
            New chat
          </button>
        </div>
        {salesMoveId ? (
          <div className="border-b border-border bg-muted/15 px-4 py-2.5">
            <button
              type="button"
              onClick={() => navigate(todayContextHref)}
              className="group flex max-w-full items-start gap-2 text-left"
            >
              <ArrowLeft className="mt-0.5 size-3.5 shrink-0 text-muted-foreground transition group-hover:text-foreground" />
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium text-foreground group-hover:underline">
                  Context from Today
                  {salesMove ? ` · ${salesMove.recommendedAction}` : ""}
                </span>
                <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                  {salesMove
                    ? [salesMove.accountName, salesMove.relevantPerson?.name]
                        .filter(Boolean)
                        .join(" · ")
                    : salesMoveQuery.isLoading
                      ? "Loading Command Item…"
                      : "Command Item is no longer available · Back to bucket"}
                </span>
              </span>
            </button>
            {salesMove ? (
              <div className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5">
                {SALES_MOVE_STARTERS.map((label) => (
                  <button
                    key={label}
                    type="button"
                    disabled={running || resolvingCommandThread}
                    onClick={() => void send(label)}
                    className="shrink-0 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium hover:bg-muted/30 disabled:opacity-50"
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          {showEmpty ? (
            <AssistantEmptyState
              contextProductIds={contextProductIds}
              onContextChange={(ids) => void persistContext(ids)}
              onSend={(m) => void send(m)}
              onPreset={(p) => void onPreset(p)}
              running={running || resolvingCommandThread}
            />
          ) : (
            <AssistantThreadView
              threadId={threadId ?? "new"}
              messages={messages}
              streaming={streamState}
              onMessagesChange={setMessages}
            />
          )}
        </div>

        {!showEmpty && (
          <div className="border-t border-border px-4 py-3">
            <div className="mx-auto flex max-w-2xl flex-col items-center gap-2">
              <AssistantComposer
                placeholder="Reply to Assistant"
                disabled={running || resolvingCommandThread}
                contextProductIds={contextProductIds}
                onContextChange={(ids) => void persistContext(ids)}
                onSend={(m) => void send(m)}
              />
              <p className="text-center text-[11px] text-muted-foreground">
                Chats may be shared with third parties, see{" "}
                <button
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={() => navigate("/settings/ai")}
                >
                  AI Policy
                </button>
                . Avoid sharing sensitive info.
              </p>
              {threadId && (
                <p className="font-mono text-[10px] text-faint">ID {threadId}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <CreditSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}
